import type { ZitadelUserProfile } from './types';

/**
 * Extract tenant and organization slugs from user profile
 */
export function extractSlugs(profile: ZitadelUserProfile | null): {
  tenantSlug: string | null;
  organizationSlug: string | null;
} {
  if (!profile) {
    return { tenantSlug: null, organizationSlug: null };
  }

  // First, try custom claims (set by Zitadel Actions)
  let tenantSlug = profile.tenantSlug || null;
  const organizationSlug = profile.organizationSlug || null;

  // Fallback: derive tenantSlug from organization primary domain
  if (!tenantSlug && profile['urn:zitadel:iam:org:domain:primary']) {
    const primaryDomain = profile['urn:zitadel:iam:org:domain:primary'];
    // Extract first part of domain (e.g., "acme" from "acme.zitadel.localhost")
    tenantSlug = primaryDomain.split('.')[0] || null;
  }

  return { tenantSlug, organizationSlug };
}

/**
 * Extract roles from Zitadel JWT claims
 *
 * Zitadel stores roles in two possible formats:
 * 1. Generic: "urn:zitadel:iam:org:project:roles"
 * 2. Project-specific: "urn:zitadel:iam:org:project:{PROJECT_ID}:roles"
 *
 * Both contain: { "role_name": { "org_id": "org_id_value" } }
 *
 * This function extracts the role names (keys) from both claim formats.
 */
export function extractRoles(profile: ZitadelUserProfile | null): string[] {
  if (!profile) {
    return [];
  }

  // Try generic claim first
  const rolesObj = profile['urn:zitadel:iam:org:project:roles'];
  if (rolesObj && typeof rolesObj === 'object') {
    return Object.keys(rolesObj);
  }

  // Fallback: search for project-specific role claims
  // e.g. "urn:zitadel:iam:org:project:359001218992832518:roles"
  const rolePrefix = 'urn:zitadel:iam:org:project:';
  const roleSuffix = ':roles';
  for (const key of Object.keys(profile)) {
    if (
      key.startsWith(rolePrefix) &&
      key.endsWith(roleSuffix) &&
      key !== 'urn:zitadel:iam:org:project:roles'
    ) {
      const value = (profile as unknown as Record<string, unknown>)[key];
      if (value && typeof value === 'object') {
        return Object.keys(value as Record<string, unknown>);
      }
    }
  }

  return [];
}
